import { useInvestments } from '../queries';
import { useTranslation } from 'react-i18next';
import {
  getGridSumTableColumnsForInvestments,
  getInvalidateInvestmentProactivelyOptions,
  getPreTaxSavingsInvestments,
  getTotalRecurringContributionsForInvestments,
} from '../util';
import { RecurringFrequency } from '../../generated/graphql';
import { recurringMonetaryAmountConverter } from '../../util';
import { useDisplayRecurringMonetaryAmount } from '../../DataPoint';
import { InvestmentSumTableArgs } from '../types';
import { useQueryClient } from '@tanstack/react-query';
import { investmentKeys } from '../queryKeys';
import { UseEditInvestmentModalFormParams } from './useEditInvestmentModalForm';

export interface UsePreTaxSavingsContributionsSumTableArgs
  extends Pick<InvestmentSumTableArgs, 'onClickItem'>,
    Pick<UseEditInvestmentModalFormParams, 'afterUpdate'> {
  householdID: string | undefined | null;
}

export function usePreTaxSavingsContributionsSumTable({
  householdID,
  onClickItem,
  afterUpdate,
}: UsePreTaxSavingsContributionsSumTableArgs) {
  const { t: tInvestment } = useTranslation('investment');
  const { t: tUI } = useTranslation('UI');
  const { investments, investmentsQuery } = useInvestments({ householdID });
  const { displayRecurringMonetaryAmount } = useDisplayRecurringMonetaryAmount();

  const isLoading = investmentsQuery.isLoading;
  const preTaxInvestments = getPreTaxSavingsInvestments(investments);
  const annualContributions = getTotalRecurringContributionsForInvestments(preTaxInvestments, {
    frequency: RecurringFrequency.Annually,
  });
  const monthlyContributionsDisplay = displayRecurringMonetaryAmount(
    recurringMonetaryAmountConverter(annualContributions, RecurringFrequency.Monthly)
  );
  const queryClient = useQueryClient();
  const columns = getGridSumTableColumnsForInvestments({
    tInvestment,
    onClickItem,
    tUI,
    afterUpdate: async (investment) => {
      await queryClient.invalidateQueries(getInvalidateInvestmentProactivelyOptions(investment));
      await queryClient.invalidateQueries(investmentKeys.investments(householdID));
      await queryClient.invalidateQueries(investmentKeys.investmentTypes(householdID));
      await queryClient.invalidateQueries({
        queryKey: investmentKeys.investment({ householdID, investmentID: investment.id }),
        refetchType: 'all',
      });
      await afterUpdate?.(investment);
    },
  });

  return {
    isLoading,
    tInvestment,
    annualContributions,
    monthlyContributionsDisplay,
    preTaxInvestments,
    columns,
  };
}
